import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import ImagePlaceholder from "@/components/site/ImagePlaceholder";

const projects = [
    {
        title: "Wohnhaus am Hang",
        location: "Zürich · 2024",
        tag: "Neubau",
        img: "https://images.unsplash.com/photo-1778084025711-64d04aea3cfe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwY29uY3JldGUlMjBhcmNoaXRlY3R1cmUlMjBzd2l0emVybGFuZHxlbnwwfHx8fDE3ODM2OTMwNTV8MA&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Fassadensanierung",
        location: "Basel · 2024",
        tag: "Sanierung",
        img: "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwxfHxwcmVtaXVtJTIwbW9kZXJuJTIwaG91c2UlMjBjb25zdHJ1Y3Rpb24lMjByZW5vdmF0aW9ufGVufDB8fHx8MTc4MzY5MzA1NXww&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Umbau Altbau",
        location: "Bern · 2023",
        tag: "Umbau",
        img: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwbW9kZXJuJTIwaG91c2UlMjBjb25zdHJ1Y3Rpb24lMjByZW5vdmF0aW9ufGVufDB8fHx8MTc4MzY5MzA1NXww&ixlib=rb-4.1.0&q=85",
    },
    {
        title: "Sichtbeton Villa",
        location: "Luzern · 2023",
        tag: "Neubau",
        img: "https://images.unsplash.com/photo-1573652636601-d6fdcfc59640?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBzd2lzcyUyMGFyY2hpdGVjdHVyZSUyMGJ1aWxkaW5nJTIwZmluaXNoZWR8ZW58MHx8fHwxNzgzNjkzMDU1fDA&ixlib=rb-4.1.0&q=85",
    },
];

function useReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("in-view"); }),
            { threshold: 0.1 }
        );
        el.querySelectorAll(".fade-up").forEach((n) => io.observe(n));
        return () => io.disconnect();
    }, []);
    return ref;
}

export default function References() {
    const ref = useReveal();
    return (
        <section
            id="referenzen"
            ref={ref}
            data-testid="references-section"
            className="relative bg-[#FEFEFE] py-28 lg:py-40 overflow-hidden"
        >
            {/* Hintergrundbild: eigenes Mauerwerk-Foto, sehr dezent */}
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage:
                        "url('https://customer-assets-rejwkqb3.emergentagent.net/job_foundation-forward-2/artifacts/jdow140q_image.png')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundAttachment: "fixed",
                    opacity: 0.4,
                    filter: "grayscale(0.15)",
                }}
            />
            {/* Weiche weisse Übergänge oben/unten für sauberen Übergang zu Nachbarsektionen */}
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{
                    background:
                        "linear-gradient(to bottom, rgba(254,254,254,0.9) 0%, rgba(254,254,254,0.35) 22%, rgba(254,254,254,0.35) 78%, rgba(254,254,254,0.95) 100%)",
                }}
            />

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-20 mb-16 lg:mb-24 items-end">
                    <div className="lg:col-span-7">
                        <div className="eyebrow fade-up mb-6">Referenzen</div>
                        <h2
                            className="fade-up text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-[#14100D] leading-[1.02]"
                            style={{ fontFamily: "Outfit, sans-serif" }}
                        >
                            Ausgewählte
                            <br />
                            <span className="font-medium">Arbeiten.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 lg:col-start-9">
                        <p className="fade-up text-[15px] lg:text-[17px] leading-relaxed text-[#14100D]/70">
                            Ein kleiner Auszug aus unseren jüngsten Projekten. Jedes davon steht für unseren Anspruch an Präzision und Beständigkeit.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
                    {projects.map((p, i) => (
                        <Link
                            to="/referenzen"
                            key={p.title}
                            data-testid={`reference-item-${i}`}
                            className={`fade-up group block ${i % 2 === 1 ? "md:mt-24" : ""}`}
                            style={{ transitionDelay: `${i * 60}ms` }}
                        >
                            <div className="ref-image aspect-[4/5] lg:aspect-[4/5]">
                                <ImagePlaceholder />
                            </div>
                            <div className="mt-6 flex items-start justify-between gap-6">
                                <div>
                                    <h3
                                        className="text-2xl lg:text-3xl font-medium text-[#14100D] group-hover:text-[#E20514] transition-colors duration-500"
                                        style={{ fontFamily: "Outfit, sans-serif" }}
                                    >
                                        {p.title}
                                    </h3>
                                    <div className="mt-2 text-[13px] tracking-[0.18em] uppercase text-[#14100D]/60">
                                        {p.location}
                                    </div>
                                </div>
                                <span className="text-[11px] tracking-[0.28em] uppercase text-[#E20514] pt-2 whitespace-nowrap">
                                    {p.tag}
                                </span>
                            </div>
                        </Link>
                    ))}
                </div>

                <div className="mt-16 lg:mt-24 flex justify-center">
                    <Link
                        to="/referenzen"
                        data-testid="references-view-all"
                        className="btn-secondary inline-flex items-center gap-3 px-8 py-4 text-[12px] tracking-[0.24em] uppercase font-semibold"
                        style={{ borderRadius: 2 }}
                    >
                        Alle Referenzen ansehen <ArrowUpRight size={16} />
                    </Link>
                </div>
            </div>
        </section>
    );
}
